import {
    ExternalAccountSyncStatus, IExternalAccountSyncAudit, IExternalAccountSyncDetail,
    IExternalAccountSyncRecord, IExternalAccountSyncSummary
} from '../../models/ExternalAccountSync/IExternalAccountSync';

// Demo-only fallback data, shown when the real API has no records yet (e.g. before the
// ExternalAccountSyncStaging table is deployed/populated). This module is never imported by
// anything outside ExternalAccountSyncDashboard.tsx and has no effect once the API starts
// returning real data.

const DEMO_ACTION_BY_EMAIL = 'amuthu@armsolutions.com';
const DEMO_CLIENT_CODES = ['ARMSOL', 'AAIPST', 'STYSHR', 'WKNPST'];

let nextId = 1;
const id = () => nextId++;

const isoDaysAgo = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
};

interface MockSeed {
    recordSource: 'NewAccount' | 'UpdateTransaction';
    clientCode: string;
    externalCustomerId: string;
    externalTransactionId?: string;
    dbtNo?: string;
    phase?: string;
    recordType?: string;
    commercial?: string;
    firstName?: string;
    lastName?: string;
    businessName?: string;
    totalDue?: number;
    amount?: number;
    status: ExternalAccountSyncStatus;
    daysAgo: number;
}

const seeds: MockSeed[] = [
    { recordSource: 'NewAccount', clientCode: 'ARMSOL', externalCustomerId: '196588', phase: 'DBAJ', commercial: 'Y', businessName: 'Grace United Methodist Church', totalDue: 787.00, status: 'Pending', daysAgo: 0 },
    { recordSource: 'NewAccount', clientCode: 'ARMSOL', externalCustomerId: '159433', phase: 'PREC', commercial: 'Y', businessName: 'Flex Deck Inc.', totalDue: 945.96, status: 'Pending', daysAgo: 0 },
    { recordSource: 'NewAccount', clientCode: 'AAIPST', externalCustomerId: '160425', phase: 'PREC', commercial: 'Y', businessName: 'EIG Global Energy Partners', totalDue: 1550.00, status: 'Pending', daysAgo: 1 },
    { recordSource: 'NewAccount', clientCode: 'AAIPST', externalCustomerId: '118855651', phase: 'PREC', commercial: 'N', firstName: 'Loretta', lastName: 'Valencia', totalDue: 360.00, status: 'Pending', daysAgo: 1 },
    { recordSource: 'NewAccount', clientCode: 'STYSHR', externalCustomerId: '123214924', phase: 'PREC', commercial: 'N', firstName: 'Sanjay', lastName: 'Verbeek', totalDue: 176.00, status: 'Pending', daysAgo: 2 },
    { recordSource: 'NewAccount', clientCode: 'STYSHR', externalCustomerId: '111934427', phase: 'PREC', commercial: 'Y', businessName: 'Temple Ahavat Shalom', totalDue: 460.00, status: 'Approved', daysAgo: 3 },
    { recordSource: 'NewAccount', clientCode: 'WKNPST', externalCustomerId: '114659650', phase: 'PREC', commercial: 'Y', businessName: 'Blue Peak Realty', totalDue: 8353.10, status: 'Processed', daysAgo: 5 },
    { recordSource: 'NewAccount', clientCode: 'WKNPST', externalCustomerId: '107504742', phase: 'PREC', commercial: 'N', firstName: 'Peter', lastName: 'Boscarino', totalDue: 432.70, status: 'Processed', daysAgo: 6 },
    { recordSource: 'NewAccount', clientCode: 'ARMSOL', externalCustomerId: '17457481', phase: 'PREC', commercial: 'N', firstName: 'Monica', lastName: 'Fiato', totalDue: 225.00, status: 'Rejected', daysAgo: 4 },
    { recordSource: 'NewAccount', clientCode: 'AAIPST', externalCustomerId: '273824', phase: 'CBAJ', commercial: 'N', firstName: 'David', lastName: 'Davis', totalDue: 10.00, status: 'Rejected', daysAgo: 4 },
    { recordSource: 'UpdateTransaction', clientCode: 'ARMSOL', externalCustomerId: '150864', externalTransactionId: '105007', dbtNo: '007654773', recordType: 'CPMT', amount: 49.00, status: 'Pending', daysAgo: 0 },
    { recordSource: 'UpdateTransaction', clientCode: 'AAIPST', externalCustomerId: '159433', externalTransactionId: '103894', dbtNo: '007624532', recordType: 'CPMT', amount: 55.00, status: 'Pending', daysAgo: 0 },
    { recordSource: 'UpdateTransaction', clientCode: 'STYSHR', externalCustomerId: '169911', externalTransactionId: '104469', dbtNo: '007621418', recordType: 'CPMT', amount: 27.06, status: 'Pending', daysAgo: 1 },
    { recordSource: 'UpdateTransaction', clientCode: 'WKNPST', externalCustomerId: '273824', externalTransactionId: '104173', dbtNo: '007623485', recordType: 'CBAJ', amount: 10.00, status: 'Approved', daysAgo: 2 },
    { recordSource: 'UpdateTransaction', clientCode: 'ARMSOL', externalCustomerId: '421978', externalTransactionId: '104184', dbtNo: '007478178', recordType: 'CBAJ', amount: 10.00, status: 'Processed', daysAgo: 6 },
    { recordSource: 'UpdateTransaction', clientCode: 'AAIPST', externalCustomerId: '474989', externalTransactionId: '104245', dbtNo: '006865898', recordType: 'CBAJ', amount: 330.00, status: 'Processed', daysAgo: 7 },
    { recordSource: 'UpdateTransaction', clientCode: 'STYSHR', externalCustomerId: '583751', externalTransactionId: '104433', dbtNo: '007640698', recordType: 'CBAJ', amount: 100.00, status: 'Rejected', daysAgo: 3 },
];

const toMockRecord = (seed: MockSeed): IExternalAccountSyncRecord => ({
    id: id(),
    batchId: '00000000-0000-0000-0000-000000000000',
    sourceSystemCode: 'IHUB',
    recordSource: seed.recordSource,
    clientCode: seed.clientCode,
    externalCustomerId: seed.externalCustomerId,
    externalTransactionId: seed.externalTransactionId,
    dbtNo: seed.dbtNo,
    phase: seed.phase,
    recordType: seed.recordType,
    commercial: seed.commercial,
    firstName: seed.firstName,
    lastName: seed.lastName,
    businessName: seed.businessName,
    totalDue: seed.totalDue,
    amount: seed.amount,
    status: seed.status,
    receivedDate: isoDaysAgo(seed.daysAgo),
    processedDate: seed.status === 'Processed' ? isoDaysAgo(Math.max(seed.daysAgo - 1, 0)) : undefined,
});

export const getMockClientCodes = (): string[] => DEMO_CLIENT_CODES;

export const getMockRecords = (): IExternalAccountSyncRecord[] => seeds.map(toMockRecord);

export const getMockSummary = (records: IExternalAccountSyncRecord[]): IExternalAccountSyncSummary => {
    const byStatusMap = new Map<ExternalAccountSyncStatus, number>();
    const byClientMap = new Map<string, number>();

    records.forEach((r) => {
        byStatusMap.set(r.status, (byStatusMap.get(r.status) || 0) + 1);
        byClientMap.set(r.clientCode, (byClientMap.get(r.clientCode) || 0) + 1);
    });

    return {
        totalQty: records.length,
        byStatus: Array.from(byStatusMap.entries()).map(([status, qty]) => ({ status, qty })),
        byClientCode: Array.from(byClientMap.entries())
            .map(([clientCode, qty]) => ({ clientCode, qty }))
            .sort((a, b) => b.qty - a.qty),
    };
};

export const getMockDetail = (record: IExternalAccountSyncRecord): IExternalAccountSyncDetail => ({
    ...record,
    rawPayloadJson: JSON.stringify(record, null, 2),
});

export const getMockAudit = (record: IExternalAccountSyncRecord): IExternalAccountSyncAudit[] => {
    if (record.status === 'Pending') return [];

    const entries: IExternalAccountSyncAudit[] = [];
    if (record.status === 'Approved' || record.status === 'Processed') {
        entries.push({
            id: 1,
            stagingId: record.id,
            action: 'Approved',
            actionByEmail: DEMO_ACTION_BY_EMAIL,
            actionDate: record.processedDate || record.receivedDate,
        });
    }
    if (record.status === 'Rejected') {
        entries.push({
            id: 1,
            stagingId: record.id,
            action: 'Rejected',
            actionByEmail: DEMO_ACTION_BY_EMAIL,
            actionDate: record.receivedDate,
            rejectReason: 'Duplicate Record',
            rejectComments: 'Matched an existing account already on file.',
        });
    }
    return entries;
};
